import { inject, Injectable } from '@angular/core';
import { IndicatorTreeNode } from './models/IndicatorTreeNode';
import { Indicator } from './models/indicator';
import { IndicatorStore } from './stores/indicator/indicator.state';

@Injectable({
  providedIn: 'root',
})
export class IndicatorTreeHelperService {
  readonly indicatorStore = inject(IndicatorStore);
  private cachedExpandedStates?: { [key: string]: boolean };
  sessionStorage: Storage | undefined;
  groupedChildren!: Record<string, Indicator[]>;

  constructor() {
    this.sessionStorage = document.defaultView?.sessionStorage;
    this.setExpandedState();
  }

  /**
   * Initializes the tree service by setting up grouped children
   * using data from the indicator store.
   *
   * @return {void}
   */
  initTreeService(): void {
    this.groupedChildren = this.indicatorStore.groupedChildren();
  }

  /**
   * Sets the expanded state of the tree by retrieving and parsing the stored expanded states
   * from session storage. The parsed data is assigned to `cachedExpandedStates`.
   *
   * @return {void} Does not return anything.
   */
  setExpandedState() {
    this.cachedExpandedStates = JSON.parse(
      this.sessionStorage?.getItem('atlasTreeExpandedState') || '{}'
    );
  }

  /**
   * Retrieves the children of a tree node based on the specified parent ID.
   *
   * @param {string} parentId - The unique identifier of the parent tree node.
   * @param {boolean} asTable - Specifies whether the output should be in table format.
   * @param {Record<string, Indicator[]>} [groupedChildren] - Optional grouping of child indicators.
   * @return {IndicatorTreeNode[]}
   */
  getTreeNodeChildren = (
    parentId: string,
    asTable: boolean,
    groupedChildren?: Record<string, Indicator[]>
  ): IndicatorTreeNode[] => {
    const childrenMap =
      groupedChildren || this.indicatorStore.groupedChildren();
    if (!childrenMap[parentId]) return [];
    return childrenMap[parentId].map((indicatorChild: Indicator) =>
      this.transformIndicatorToHierarchicalItem(
        indicatorChild,
        asTable,
        childrenMap
      )
    );
  };

  /**
   * Transforms an indicator into a tree node item with hierarchical structure.
   *
   * @param {Indicator} indicator - The indicator object to be transformed.
   * @param {boolean} asTable - A flag indicating if the output should include additional table-related properties.
   * @param {Record<string, Indicator[]>} [groupedChildren] - Optional grouping of child indicators.
   * @returns {IndicatorTreeNode} A tree node representation of the given indicator.
   */
  transformIndicatorToHierarchicalItem = (
    indicator: Indicator,
    asTable: boolean,
    groupedChildren?: Record<string, Indicator[]>
  ): IndicatorTreeNode => {
    const children = this.getTreeNodeChildren(
      indicator._id,
      asTable,
      groupedChildren
    );
    return asTable
      ? {
          data: {
            _id: indicator._id || '',
            key: indicator._id || '',
            label: indicator.name,
            unit: indicator.unit,
            type: indicator.isSet ? 'indicator-set' : 'indicator',
          },
          _id: indicator._id || '',
          key: indicator._id || '',
          label: indicator.name,
          sorting: indicator.sorting,
          icon: indicator.isSet ? 'fas fa-folder' : 'fas fa-ruler-vertical',
          type: indicator.isSet ? 'indicator-set' : 'indicator',
          category: indicator.category,
          children: children,
        }
      : {
          _id: indicator._id || '',
          key: indicator._id || '',
          label: indicator.name,
          locked: indicator.locked,
          icon: indicator.isSet ? 'fas fa-folder' : 'fas fa-ruler-vertical',
          type: indicator.isSet ? 'indicator-set' : 'indicator',
          category: indicator.category,
          unit: indicator.unit,
          children: children,
          sorting: indicator.sorting,
          droppable: false,
        };
  };

  /**
   * Merges a set of indicators into a hierarchical category tree structure.
   * Ensures that indicators are properly added into the tree nodes and avoids duplicates.
   * Recursively processes child nodes to maintain hierarchy integrity.
   *
   * @param {IndicatorTreeNode[]} tree - The category tree structure to which the indicators will be merged.
   *                                      Each node contains category-specific information.
   * @param {Record<string, Indicator[]>} groupedIndicators - A record containing indicators grouped by their parent category ID.
   * @param {boolean} [asTable=false] - A flag indicating whether the transformation of indicators should be displayed in table form.
   * @param {Record<string, Indicator[]>} [groupedChildrenOverride] - Optional grouping of child indicators.
   * @return {IndicatorTreeNode[]} The updated tree structure with merged indicators.
   */
  mergeIndicatorsWithCategoryTree(
    tree: IndicatorTreeNode[],
    groupedIndicators: Record<string, Indicator[]>,
    asTable = false,
    groupedChildrenOverride?: Record<string, Indicator[]>
  ) {
    return tree.map(node => {
      const newNode = { ...node }; // Shallow copy of the node

      if (groupedIndicators[node._id]) {
        const indicatorChildren = groupedIndicators[node._id]
          .filter(indicator => {
            return !indicator.parent;
          })
          .map(indicator =>
            this.transformIndicatorToHierarchicalItem(
              indicator,
              asTable,
              groupedChildrenOverride
            )
          )
          .sort((a, b) => a.sorting - b.sorting);
        // Check if the indicatorChildren already exist in node.children
        if (!newNode.children) {
          newNode.children = []; // Initialize children array if it's empty
        }

        const existingIds = new Set(newNode.children.map(child => child._id));
        const nonDuplicateIndicators = indicatorChildren.filter(
          child => !existingIds.has(child._id)
        );

        // Add only non-duplicate children
        newNode.children = [...newNode.children, ...nonDuplicateIndicators];
      }

      // Recursively merge indicators for subcategories
      if (newNode.children.length > 0) {
        newNode.children = this.mergeIndicatorsWithCategoryTree(
          newNode.children,
          groupedIndicators,
          asTable,
          groupedChildrenOverride
        );
        newNode.expanded = this.cachedExpandedStates?.[newNode.key] || false;
      }
      return newNode; // Return the newly constructed node
    });
  }

  /**
   * Filters categories that either have indicators or have children with indicators.
   * @param categories - The initial category tree.
   * @param indicatorsByCategory - A mapping of category ID to indicators.
   * @returns The filtered category tree.
   */
  filterCategoriesWithIndicators(
    categories: IndicatorTreeNode[],
    indicatorsByCategory: Record<string, Indicator[]>
  ): IndicatorTreeNode[] {
    return categories
      .map(category => {
        // Check if the current category has indicators
        const hasIndicators = indicatorsByCategory[category._id];

        // Recursively filter child categories
        const filteredChildren = this.filterCategoriesWithIndicators(
          category.children,
          indicatorsByCategory
        );

        // Include this category if it has indicators or if any children have indicators
        if (hasIndicators || filteredChildren.length > 0) {
          return {
            ...category,
            children: filteredChildren,
          };
        }
        // Exclude categories without indicators or children with indicators
        return null;
      })
      .filter(category => category !== null) as IndicatorTreeNode[];
  }

  /**
   * Applies hierarchical numbering to category nodes in the tree.
   * Indicators are skipped as they don't require numbering.
   *
   * @param {IndicatorTreeNode[]} nodes - The array of tree nodes to number.
   * @param {string} [prefix=''] - The numbering prefix from parent levels.
   * @return {void}
   */
  applyNumbering(nodes: IndicatorTreeNode[], prefix: string = ''): void {
    let categoryCount = 0;
    nodes.forEach(node => {
      if (node.type === 'category') {
        categoryCount++;
        const currentNumber = prefix
          ? `${prefix}.${categoryCount}`
          : `${categoryCount}`;

        node.number = currentNumber;

        if (node.children && node.children.length > 0) {
          this.applyNumbering(node.children, currentNumber);
        }
      }
    });
  }
}
